// src/pages/shelbula/workspaces/StatusDisplay.jsx
import React from 'react';
import { CheckCircle, AlertCircle, Loader, Activity, Clock, ShipWheel, RotateCcw, XCircle, Edit } from 'lucide-react';

const StatusDisplay = ({ status, isBadge = false, isDot = false }) => {
    const baseClasses = isBadge 
        ? "px-2.5 py-1 text-xs font-bold rounded-full inline-flex items-center gap-1.5 whitespace-nowrap" 
        : "flex items-center gap-1.5 text-xs whitespace-nowrap";

    const styles = {
        COMPLETED:        { style: 'bg-[rgba(var(--success-rgb),0.15)] border border-[rgba(var(--success-rgb),0.3)] text-[rgb(var(--success-rgb))]', dot: 'bg-green-500' },
        FAILED:           { style: 'bg-[rgba(var(--error-rgb),0.15)] border border-[rgba(var(--error-rgb),0.3)] text-[rgb(var(--error-rgb))]',   dot: 'bg-red-500' },
        DENIED:           { style: 'bg-[rgba(var(--error-rgb),0.15)] border border-[rgba(var(--error-rgb),0.3)] text-[rgb(var(--error-rgb))]',   dot: 'bg-red-500' },
        EXECUTING:        { style: 'bg-[rgba(var(--primary-light-rgb),0.15)] border border-[rgba(var(--primary-light-rgb),0.3)] text-[rgb(var(--primary-light-rgb))]', dot: 'bg-blue-500' },
        PLANNING:         { style: 'bg-[rgba(192,132,252,0.15)] border border-[rgba(192,132,252,0.3)] text-[rgb(192,132,252)]', dot: 'bg-blue-500' },
        PENDING_APPROVAL: { style: 'bg-[rgba(var(--warning-color-rgb),0.15)] border border-[rgba(var(--warning-color-rgb),0.3)] text-[rgb(var(--warning-color-rgb))]', dot: 'bg-yellow-500' },
        RE_PLANNING:      { style: 'bg-[rgba(var(--warning-color-rgb),0.15)] border border-[rgba(var(--warning-color-rgb),0.3)] text-[rgb(var(--warning-color-rgb))]', dot: 'bg-yellow-500' },
        ROLLED_BACK:      { style: 'bg-[rgba(var(--on-surface-dim-rgb),0.15)] border border-[rgba(var(--on-surface-dim-rgb),0.3)] text-[rgb(var(--on-surface-dim-rgb))]', dot: 'bg-gray-400' },
        CANCELLED:        { style: 'bg-[rgba(var(--on-surface-dim-rgb),0.15)] border border-[rgba(var(--on-surface-dim-rgb),0.3)] text-[rgb(var(--on-surface-dim-rgb))]', dot: 'bg-gray-500' },
        CANCELLED_AND_REVERTED: { style: 'bg-[rgba(var(--on-surface-dim-rgb),0.15)] border border-[rgba(var(--on-surface-dim-rgb),0.3)] text-[rgb(var(--on-surface-dim-rgb))]', dot: 'bg-gray-400' },
        IDLE:             { style: 'bg-[rgba(var(--on-surface-dim-rgb),0.15)] border border-[rgba(var(--on-surface-dim-rgb),0.3)] text-[rgb(var(--on-surface-dim-rgb))]', dot: 'bg-gray-500' },
        default:          { style: 'bg-[rgba(var(--on-surface-dim-rgb),0.15)] border border-[rgba(var(--on-surface-dim-rgb),0.3)] text-[rgb(var(--on-surface-dim-rgb))]', dot: 'bg-gray-500' }
    };
    // ARCHITECTURAL FIX: Treat 'active' status as 'IDLE'
    styles.active = styles.IDLE;

    const textMap = {
        ROLLED_BACK: 'Rolled Back', CANCELLED: 'Cancelled', CANCELLED_AND_REVERTED: 'Reverted',
        PENDING_APPROVAL: 'Review Plan', RE_PLANNING: 'Re-Planning', COMPLETED: 'Completed',
        FAILED: 'Failed', DENIED: 'Denied', EXECUTING: 'Executing', PLANNING: 'Planning', IDLE: 'Idle',
        active: 'Idle'
    };
    
    const iconMap = {
        COMPLETED: CheckCircle, FAILED: AlertCircle, DENIED: XCircle, EXECUTING: Loader,
        PENDING_APPROVAL: ShipWheel,
        RE_PLANNING: Edit, 
        PLANNING: ShipWheel, 
        ROLLED_BACK: RotateCcw,
        CANCELLED: XCircle, 
        CANCELLED_AND_REVERTED: RotateCcw, 
        IDLE: Clock,
        active: Clock
    };

    const statusKey = status || 'IDLE';
    const config = styles[statusKey] || styles.default;
    const displayText = textMap[statusKey] || statusKey.replace(/_/g, ' ') || 'Idle';
    const Icon = iconMap[statusKey] || Clock;
    const needsAnimation = ['EXECUTING', 'PLANNING'].includes(statusKey);
    
    if (isDot) {
        return (
            <div className="flex items-center justify-center w-4 h-4" title={displayText}>
                <div className={`w-2 h-2 rounded-full ${config.dot} ${needsAnimation ? 'animate-pulse' : ''}`} />
            </div>
        );
    }

    const classes = isBadge ? config.style : `text-[${config.dot}]`; // Simplified for text-only
    const iconSize = isBadge ? 13 : 13;

    return (
        <span className={`${baseClasses} ${classes}`}>
            <Icon size={iconSize} className={needsAnimation ? "animate-spin" : ""} />
            {displayText}
        </span>
    );
};

export default StatusDisplay;